// UserList.js
import React, { useEffect, useState } from 'react';
import {
	Table,
	Tabs,
	Button,
	Divider,
	Empty,
	Icon,
	Row,
	Col,
	Popconfirm,
	message,
	Card,
	Menu,
	Dropdown,
	Tooltip,
} from 'antd';
import { getStringSorter, getBooleanSorter, getIntSorter } from '../../utils/antUtils.js';
import {
	getUsers,
	getUser,
	getRAGTeam,
	deleteUser,
	createUser,
	createAddOns,
	deleteAddOns,
	updateUser,
	updateUserStatus,
	restoreUser,
	getRAGUser,
	postRAGUser,
	toggleRAGUserActiveStatus,
	updateRAGUserDefaultModel,
	updateRAGUserIsTestMode,
} from '../../utils/api.js';
import UserModal from './userModal.jsx';
import SocratesUserModal from '../socratesUserModal.jsx';
import { ENV } from '../../utils/http.js';
import { buildAddOns } from '../../utils/addOnsHelper.js';
import AddOnTags from '../AddOnTags.jsx';
import OrganizationChangeModal from '../organization/organizationChangeModal.jsx';
import iconStyle from '../../components/icon.cs.js';

const { Column } = Table;
const { TabPane } = Tabs;

const UserList = ({ orgId, createNew, title, onChange }) => {
	const [users, setUsers] = useState(null);
	const [sharedLogins, setSharedLogins] = useState(null);
	const [loading, setLoading] = useState(true);
	const [selectedTab, setSelectedTab] = useState('shared');
	const [changeModal, setChangeModal] = useState({
		user: null,
		open: false,
	});
	const [userModal, setUserModal] = useState({
		mode: 'create',
		accountType: null,
		open: false,
		user: null,
	});
	const [socratesModal, setSocratesModal] = useState({
		open: false,
		defaultModel: 'gpt-4o',
		testMode: false,
		ragUserId: null,
	});

	const loadData = async () => {
		setLoading(true);
		const res = (await getUsers(orgId)) || [];

		if (res.length > 0) {
			const newUsers = res.filter((user) => user && user.email.includes('@'));
			const newSharedLogins = res.filter((item) => item && item.email && !item.email.includes('@'));

			setUsers(newUsers);
			setSharedLogins(newSharedLogins);
		} else {
			setUsers([]);
			setSharedLogins([]);
		}
		setLoading(false);
	};

	useEffect(() => {
		loadData();
	}, [orgId]);

	const loginAsUser = async (id, e) => {
		e.preventDefault();
		e.stopPropagation();

		const res = await getUser(id);

		if (res) {
			window.open(`${ENV.previewUrl}/loggain/${res.confirmationKey}`, '_blank');
		}
	};

	const handleUserEdit = (user, accountType) => {
		setUserModal({
			user: {
				...user,
				addOns: buildAddOns(user),
			},
			mode: 'edit',
			accountType: accountType,
			open: true,
		});
	};

	const openSocratesModal = async (orgId, userId) => {
		let team = null;

		try {
			team = await getRAGTeam(orgId);
		} catch (error) {
			message.warning('Socrates plustjänst ej aktiv för kund.', 7);
			return;
		}

		if (team && team.isActive) {
			try {
				const ragUser = await getRAGUser(team.id, userId);
				if (ragUser && ragUser.isActive) {
					setSocratesModal({
						open: true,
						defaultModel: ragUser.defaultModel,
						testMode: ragUser.isTestMode,
						ragUserId: ragUser.id,
					});
				}
			} catch (error) {
				// RAG user missing
				message.warning('Socrates plustjänst ej aktiv för kontot.', 7);
			}
		}
	};

	const handleUserDelete = async (user) => {
		try {
			await deleteUser(user.id);

			message.success(`Konto ${user.id} markerat för radering`);
		} catch (error) {
			message.error('Kunde inte radera kontot');
		} finally {
			await loadData();
		}
	};

	const handleUserRestore = async (user) => {
		try {
			await restoreUser(user.id);

			message.success(`Konto ${user.id} återställt`);
		} catch (error) {
			message.error(`Kunde inte återställa kontot`);
		} finally {
			await loadData();
		}
	};

	const handleUserToggleStatus = async (user) => {
		try {
			const { id, enabled } = user;

			await updateUserStatus(id, !enabled);

			message.success(`Konto ${user.id} ${!enabled ? 'aktiverat' : 'deaktiverat'}`);
		} catch (error) {
			message.error('Kunde inte uppdatera kontot');
		} finally {
			await loadData();
		}
	};

	const handleCreateUser = (accountType = null) => {
		setUserModal({
			user: null,
			mode: 'create',
			accountType,
			open: true,
		});
	};

	const handleUserModalSubmit = async (formData) => {
		const { addOns, ...formValues } = formData;
		const { user, mode } = userModal;

		const userData = {
			email: formValues.email || '',
			licenseKey: formValues.licenseKey || '',
			admin: formValues.admin || false,
			firstName: formValues.firstName || '',
			lastName: formValues.lastName || '',
			organizationID: orgId,
			enabled: formValues.enabled || false,
			expiryDate: formValues.expiryDate ? formValues.expiryDate.format('YYYY-MM-DD') : '2099-01-01',
			sbtPlus: (addOns && addOns.includes('LEGAL_CASE_PLUS')) || false,
		};

		const handleSocratesUserManagement = async (userId) => {
			let keepSocratesAddOn = true;

			// Handle Socrates RAG user and add-on
			if (addOns && addOns.includes('SOCRATES')) {
				try {
					const team = await getRAGTeam(orgId);
					if (team && team.isActive) {
						try {
							const ragUser = await getRAGUser(team.id, userId);
							if (!ragUser.isActive) {
								await toggleRAGUserActiveStatus(ragUser.id);
							}
						} catch (error) {
							// RAG user missing, create RAG user
							try {
								await postRAGUser(team.id, userId, true);
							} catch (postError) {
								// If postRAGUser fails, remove SOCRATES from addOns
								keepSocratesAddOn = false;
							}
						}
					} else {
						// RAG team not active, remove SOCRATES from addOns list
						message.warning('Socrates plustjänst ej aktiv för kund.', 7);
						keepSocratesAddOn = false;
					}
				} catch (error) {
					// RAG team missing, remove SOCRATES from addOns list
					message.warning('Socrates plustjänst ej aktiv för kund.', 7);
					keepSocratesAddOn = false;
				}
			} else {
				// In-activate RAG user
				try {
					const team = await getRAGTeam(orgId);

					if (team) {
						await getRAGUser(team.id, userId).then(async (ragUser) => {
							if (ragUser.isActive) {
								await toggleRAGUserActiveStatus(ragUser.id).then((user) => {
									// RAG user activated, fall through
								});
							}
						});
					}
				} catch (error) {
					// RAG user missing, fall through
				}
				keepSocratesAddOn = false;
			}

			return keepSocratesAddOn;
		};

		const handleAddOns = async (userId, keepSocratesAddOn) => {
			if (addOns) {
				if (!keepSocratesAddOn) {
					if (addOns.includes('SOCRATES')) {
						message.warning('Socrates plustjänst har inte lagts till för kontot.', 7);
						addOns.splice(addOns.indexOf('SOCRATES'), 1);
					}
				}

				const addOnData = addOns.map((addOn) => ({
					accountID: userId,
					accountType: 'LOGIN',
					type: addOn,
				}));

				if (mode === 'edit') {
					const addOnsToRemove = user.addOns
						.filter((existingAddOn) => !addOns.includes(existingAddOn.type))
						.map((existingAddOn) => existingAddOn.id);

					if (addOnsToRemove.length) {
						await deleteAddOns(addOnsToRemove);
					}
				}

				const addOnResponse = await createAddOns(addOnData);
				if (!addOnResponse) {
					message.error('Misslyckades att skapa tillägg. Kontakta support.');
				}
			}
		};

		try {
			if (mode === 'create') {
				const newUser = await createUser(userData, userData.email === '');
				if (newUser.id) {
					message.success(`Konto ${newUser.id} skapat`);
					let keepSocratesAddOn = await handleSocratesUserManagement(newUser.id);
					await handleAddOns(newUser.id, keepSocratesAddOn);
				}
			} else if (mode === 'edit') {
				const updatedUser = await updateUser(user.id, userData, userData.email === '');
				if (updatedUser.id) {
					message.success(`Konto ${user.id} uppdaterat`);
					let keepSocratesAddOn = await handleSocratesUserManagement(updatedUser.id);
					await handleAddOns(updatedUser.id, keepSocratesAddOn);
				}
			}
		} catch (error) {
			const action = mode === 'create' ? 'skapa' : 'uppdatera';
			message.error(`Kunde inte ${action} konto`);
		}

		setUserModal({ ...userModal, open: false });
		await loadData();

		if (onChange) {
			onChange();
		}
	};

	const handleChangeOrgSubmit = async (formData) => {
		try {
			const { id } = formData;
			const { user } = changeModal;

			await updateUser(user.id, { ...user, organizationID: id });

			message.success(`Konto ${user.id} flyttat till organisation ${id}.`);
			setTimeout(() => window.location.assign(`/kund/${formData.id}`), 1000);
		} catch (error) {
			message.error('Kunde inte flytta kontot');
		}
	};

	const saveSocratesUserSettings = async (selectedModel, testMode) => {
		let currentSocratesSettings = socratesModal;

		await setSocratesModal({
			...socratesModal,
			open: false,
			defaultModel: selectedModel,
			testMode: testMode,
		});

		if (currentSocratesSettings.defaultModel !== selectedModel) {
			await updateRAGUserDefaultModel(socratesModal.ragUserId, selectedModel);
		}
		if (currentSocratesSettings.testMode !== testMode) {
			await updateRAGUserIsTestMode(socratesModal.ragUserId);
		}
	};

	const renderActions = (accountType, user, showLogin = false, showMove = false) => {
		const menu = (
			<Menu>
				<Menu.Item key='toggleStatus'>
					<a
						onClick={() => handleUserToggleStatus(user)}
						style={{ color: user.enabled ? 'orange' : 'green' }}
					>
						<Icon type='poweroff' /> {user.enabled ? 'Inaktivera' : 'Aktivera'}
					</a>
				</Menu.Item>
				{showLogin && (
					<Menu.Item key='login'>
						<a onClick={(e) => loginAsUser(user.id, e)}>
							<Icon type='login' /> Logga in
						</a>
					</Menu.Item>
				)}
				{showMove && (
					<Menu.Item key='move'>
						<a onClick={() => setChangeModal({ user, open: true })}>
							<Icon type='arrow-right' /> Flytta
						</a>
					</Menu.Item>
				)}
				{user.deletionDate && (
					<Menu.Item key='restore'>
						<a onClick={() => handleUserRestore(user)} style={{ color: 'orange' }}>
							<Icon type='undo' /> Återställ
						</a>
					</Menu.Item>
				)}
				{!user.deletionDate && (
					<Menu.Item
						key='delete'
						style={{
							backgroundColor: 'inherit',
							cursor: 'pointer',
						}}
						disabled
					>
						<Popconfirm
							title={
								<span>
									Är du säker på att du vill radera <b>{user.email}</b>?
								</span>
							}
							onConfirm={() => handleUserDelete(user)}
							okText='Ja'
							cancelText='Nej'
							okType='danger'
							okButtonProps={{ size: 'default' }}
							cancelButtonProps={{ size: 'default' }}
							placement='bottomRight'
							icon={<Icon type='delete' style={{ color: 'red' }} />}
						>
							<a
								style={{
									display: 'block',
									color: 'red',
									transition: 'all 0.3s',
								}}
								onMouseEnter={(e) => (e.target.style.backgroundColor = '#e6f7ff')}
								onMouseLeave={(e) => (e.target.style.backgroundColor = 'inherit')}
							>
								<Icon type='delete' /> Radera
							</a>
						</Popconfirm>
					</Menu.Item>
				)}
			</Menu>
		);

		return (
			<span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
				<a onClick={() => handleUserEdit(user, accountType)} style={{ marginRight: 8 }}>
					<Icon type='edit' /> Ändra
				</a>
				<Divider type='vertical' />
				<Dropdown overlay={menu} trigger={['click']} placement='bottomRight'>
					<a>
						<Icon type='ellipsis' /> Mer
					</a>
				</Dropdown>
			</span>
		);
	};

	const renderEmptyAccounts = (accountType) => (
		<Empty description={<span>Inga konton att visa</span>} style={{ padding: '5vh' }}>
			<Button type='primary' icon='plus' onClick={() => handleCreateUser(accountType)}>
				Skapa konto
			</Button>
		</Empty>
	);

	return (
		<div>
			{/* User Modal */}
			<UserModal
				mode={userModal.mode}
				user={userModal.user || {}}
				open={userModal.open}
				accountType={userModal.accountType}
				onCancel={() => setUserModal({ ...userModal, open: false })}
				onSubmit={handleUserModalSubmit}
			/>

			<SocratesUserModal
				open={socratesModal.open}
				title='Socrates användarinställningar'
				defaultModel={socratesModal.defaultModel}
				testMode={socratesModal.testMode}
				onCancel={() => setSocratesModal({ ...socratesModal, open: false })}
				onSubmit={(selectedModel, testMode) => saveSocratesUserSettings(selectedModel, testMode)}
			/>

			<Row style={{ marginBottom: 16 }}>
				<Col span={12}>
					<h2>{title || 'Konton'}</h2>
				</Col>
				<Col span={12}>
					{createNew && (
						<Button
							type='primary'
							icon='plus'
							size='default'
							onClick={() => handleCreateUser(selectedTab)}
							style={{ float: 'right' }}
						>
							Nytt konto
						</Button>
					)}
				</Col>
			</Row>

			<Card style={{ padding: 0 }} bodyStyle={{ padding: '8px 12px' }}>
				{/* Tabs and Tables */}
				<Tabs
					defaultActiveKey='shared'
					onChange={(activeKey) => setSelectedTab(activeKey)}
					animated={false}
				>
					<TabPane tab={`Gemensamma (${sharedLogins ? sharedLogins.length : 0})`} key='shared'>
						{sharedLogins && sharedLogins.length > 0 ? (
							<Table
								dataSource={sharedLogins}
								size='small'
								pagination={{
									pageSize: 10,
									position: 'bottom',
									size: 'default',
								}}
								loading={loading}
								rowKey={(record) => record.id}
								bodyStyle={sharedLogins && sharedLogins.length && { minHeight: '35vh', margin: 0 }}
								style={{ boxShadow: 'unset' }}
								rowClassName={(record) =>
									record.deletionDate ? 'default-cursor warning-row' : 'default-cursor'
								}
								fixed
							>
								<Column
									title=' '
									width={15}
									render={(text, record) => {
										const deletionDate = record && record.deletionDate;

										const icon = deletionDate ? 'warning' : 'team';
										const style = deletionDate ? { ...iconStyle, color: 'orange' } : iconStyle;
										const title = deletionDate ? `Konto raderas ${deletionDate}` : null;
										return (
											<Tooltip placement='right' title={title}>
												<Icon type={icon} style={style} />
											</Tooltip>
										);
									}}
									key='warning'
								/>
								<Column title='ID' dataIndex='id' key='id' sorter={getIntSorter('id')} />
								<Column
									title='Licensnyckel'
									dataIndex='email'
									key='email'
									sorter={getStringSorter('email')}
								/>
								<Column
									title='Plustjänster'
									dataIndex='addOns'
									key='addOns'
									render={(text, record) => <AddOnTags addOns={buildAddOns(record)} short />}
								/>
								<Column
									title='Aktiv'
									dataIndex='enabled'
									key='enabled'
									align='center'
									sorter={getBooleanSorter('enabled')}
									width={100}
									render={(value) => (value ? 'Ja' : 'Nej')}
								/>
								<Column
									title='Åtgärder'
									dataIndex='id'
									key='actions'
									width={150}
									render={(id, user) => renderActions('shared', user)}
								/>
							</Table>
						) : (
							renderEmptyAccounts('shared')
						)}
					</TabPane>
					<TabPane tab={`Personliga (${users ? users.length : 0})`} key='personal'>
						{users && users.length > 0 ? (
							<Table
								dataSource={users}
								size='small'
								pagination={{
									pageSize: 10,
									position: 'bottom',
									size: 'default',
								}}
								loading={loading}
								rowKey={(record) => record.id}
								scroll={{ x: 1500 }}
								bodyStyle={{ minHeight: '35vh' }}
								style={{ boxShadow: 'unset' }}
								rowClassName={(record) =>
									record.deletionDate ? 'default-cursor warning-row' : 'default-cursor'
								}
							>
								<Column
									title=' '
									width={30}
									render={(text, record) => {
										const deletionDate = record && record.deletionDate;

										const icon = deletionDate ? 'warning' : 'user';
										const style = deletionDate ? { ...iconStyle, color: 'orange' } : iconStyle;
										const title = deletionDate ? `Konto raderas ${deletionDate}` : null;
										return (
											<Tooltip placement='right' title={title}>
												<Icon type={icon} style={style} />
											</Tooltip>
										);
									}}
									key='warning'
								/>
								<Column title='ID' dataIndex='id' key='id' sorter={getIntSorter('id')} />
								<Column
									title='E-post'
									width={320}
									dataIndex='email'
									key='email'
									sorter={getStringSorter('email')}
								/>
								<Column
									title='Förnamn'
									dataIndex='firstName'
									key='firstName'
									sorter={getStringSorter('firstName')}
								/>
								<Column
									title='Efternamn'
									dataIndex='lastName'
									key='lastName'
									sorter={getStringSorter('lastName')}
								/>
								<Column
									title='Utgångsdatum'
									dataIndex='expiryDate'
									key='expiryDate'
									render={(value) => value || '-'}
									sorter={getStringSorter('expiryDate')}
								/>
								<Column
									title='Administratör'
									dataIndex='admin'
									key='admin'
									sorter={getBooleanSorter('admin')}
									align='center'
									render={(value) => (value ? 'Ja' : 'Nej')}
								/>
								<Column
									title='Plustjänster'
									dataIndex='addOns'
									key='addOns'
									ellipsis
									render={(text, record) => <AddOnTags addOns={buildAddOns(record)} short />}
								/>
								<Column
									title='Socrates'
									dataIndex='socrates'
									key='socrates'
									sorter={getIntSorter('socrates')}
									align='center'
									render={(value, record) => (
										<span
											style={{
												display: 'flex',
												justifyContent: 'center',
											}}
										>
											<a onClick={() => openSocratesModal(orgId, record.id)}>
												<Icon type='edit' /> Ändra
											</a>
										</span>
									)}
								/>
								<Column
									title='Aktiv'
									dataIndex='enabled'
									key='enabled'
									align='center'
									sorter={getBooleanSorter('enabled')}
									render={(value) => (value ? 'Ja' : 'Nej')}
								/>
								<Column
									title='Åtgärder'
									dataIndex='id'
									key='actions'
									width={150}
									fixed='right'
									render={(id, user) => renderActions('personal', user, true, true)}
								/>
							</Table>
						) : (
							renderEmptyAccounts('personal')
						)}
					</TabPane>
				</Tabs>
			</Card>
		</div>
	);
};

export default UserList;



// WEBPACK FOOTER //
// ./src/components/user/userList.jsx